import { ILocation, type ITaxRate, type OnlineServiceFeeLine } from '@order-up/models';
import { CreateLocationType } from '@order-up/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { locationService } from '../services/locations'; // scoped-path-ok: module path
import { readOnlineServiceFeeLine } from '../services/onlineServiceFeeLine';
import { readPublicTaxRates } from '../services/publicTaxRates';

/**
 * Defines the query keys used for restaurant-related data.
 */
export const locationKeys = {
    all: ['restaurants'] as const, // scoped-path-ok: not location-scoped (React Query key, not a storage path)
    lists: () => [...locationKeys.all, 'list'] as const,
    listByTenant: (tenantId: number) => [...locationKeys.lists(), { tenantId }] as const,
    details: () => [...locationKeys.all, 'detail'] as const,
    detailById: (id: number) => [...locationKeys.details(), id] as const,
    detailByGuid: (guid: string, tenantId?: number) => [...locationKeys.details(), guid, { tenantId }] as const,
};

/**
 * A hook to fetch all restaurants belonging to a specific tenant.
 * @param tenantId The numeric ID of the tenant.
 */
const useGetLocationsByTenantId = (tenantId: number) => {
    return useQuery({
        queryKey: locationKeys.listByTenant(tenantId),
        queryFn: () => locationService.getRestaurantsByTenantId(tenantId),
        enabled: !!tenantId,
    });
};

/**
 * A hook to fetch a single restaurant by its numeric restaurant ID.
 * @param locationNumericId The numeric ID of the restaurant.
 * @param tenantId Optional tenant ID for more efficient lookup.
 */
const useGetLocationById = (locationNumericId: number, tenantId?: number) => {
    return useQuery({
        queryKey: locationKeys.detailById(locationNumericId),
        queryFn: () => locationService.getRestaurantById(locationNumericId, tenantId),
        enabled: !!locationNumericId,
    });
};

/**
 * A hook to fetch a single restaurant by its unique GUID.
 * @param guid The GUID of the restaurant.
 * @param tenantId Optional tenant ID for more efficient lookup.
 * @param enabled Optional flag to control when the query runs.
 */
const useGetLocationByGuid = (guid: string, tenantId?: number, enabled?: boolean) => {
    return useQuery({
        queryKey: locationKeys.detailByGuid(guid, tenantId),
        queryFn: () => locationService.getRestaurantByGuid(guid, tenantId),
        enabled: (enabled !== undefined ? enabled : true) && !!guid,
    });
};

/**
 * A hook to create a new restaurant.
 * @returns A mutation object from TanStack Query.
 */
const useCreateLocation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (locationData: CreateLocationType) =>
            locationService.createRestaurant(locationData),
        // Awaited: see `useUpdateRestaurantByGuid`. A caller that navigates or
        // re-reads the list the moment this resolves must see the new row.
        onSuccess: async (data, variables) => {
            // After creating, invalidate the list of restaurants for that tenant.
            await queryClient.invalidateQueries({ queryKey: locationKeys.listByTenant(variables.tenantId) });
        },
    });
};

/**
 * A hook to update an existing restaurant.
 * @returns A mutation object from TanStack Query.
 */
const useUpdateLocation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            restaurantId: locationNumericId,
            tenantId,
            data,
        }: {
            restaurantId: number;
            tenantId: number;
            data: Partial<ILocation> | Record<string, any>;
        }) =>
            locationService.updateRestaurant(locationNumericId, tenantId, data),
        // Awaited for the same reason as `useUpdateRestaurantByGuid`: an
        // un-awaited invalidation lets `mutateAsync` resolve while the refetch is
        // still in flight, so a caller that treats "resolved" as "the new data is
        // visible" reads stale state — which is what made a settings save appear
        // to revert.
        onSuccess: async (_, variables) => {
            // After updating, invalidate the restaurant list for that tenant and by ID queries
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: locationKeys.listByTenant(variables.tenantId) }),
                queryClient.invalidateQueries({ queryKey: locationKeys.detailById(variables.restaurantId) }),
            ]);
        },
    });
};

/**
 * A hook to update an existing restaurant by GUID.
 * @returns A mutation object from TanStack Query.
 */
const useUpdateLocationByGuid = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ restaurantGuid: locationGuid, tenantGuid, data }: { restaurantGuid: string; tenantGuid: string; data: Partial<ILocation> }) =>
            locationService.updateRestaurantByGuid(locationGuid, tenantGuid, data),
        // 🔴 AWAITED, AND THAT IS THE WHOLE POINT.
        //
        // `invalidateQueries` DISCARDS the mutation's own result and schedules a
        // refetch. Returning without awaiting it lets `mutateAsync` resolve while
        // that refetch is still in flight — so a caller that treats "the promise
        // resolved" as "the server state is now visible" is wrong for as long as
        // the round trip takes.
        //
        // That is exactly how the takeout/delivery settings page appeared to
        // "save and revert": it cleared its dirty-section guard the instant
        // `mutateAsync` resolved, the guard is what stops hydration effects
        // overwriting local state, and the in-flight refetch then landed and
        // reset every field from server data that did not yet reflect the write.
        // The save had actually succeeded; the form threw the result away.
        //
        // React Query awaits a promise returned from `onSuccess` before settling
        // the mutation, so awaiting here makes "resolved" mean "the new data is
        // in the cache" for every caller of this hook.
        onSuccess: async (_, variables) => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: locationKeys.detailByGuid(variables.restaurantGuid),
                }),
                queryClient.invalidateQueries({ queryKey: locationKeys.all }),
            ]);
        },
    });
};

/**
 * A hook for syncing local data to Firestore for a specific restaurant.
 * @returns A mutation object from TanStack Query.
 */
const useSyncToFirestore = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ tenantDocId, restaurantDocId: locationDocId, data }: { tenantDocId: string; restaurantDocId: string; data: Record<string, unknown> }) =>
            locationService.syncToFirestore(tenantDocId, locationDocId, data),
        // Awaited: see `useUpdateRestaurantByGuid`.
        onSuccess: async (_, variables) => {
            // After syncing, we could invalidate queries related to the restaurant's subcollections,
            // but since we don't have those query keys defined, a general invalidation is safer.
            // Note: We use the restaurant's document ID here for invalidation context.
            await queryClient.invalidateQueries({ queryKey: [...locationKeys.details(), variables.restaurantDocId] });
        },
    });
};

/**
 * A hook for fetching all subcollection data from Firestore for a specific restaurant.
 * @param tenantDocId The Firestore-generated document ID of the tenant.
 * @param locationDocId The Firestore-generated document ID of the restaurant.
 */
const useFetchFromFirestore = (tenantDocId: string, locationDocId: string) => {
    return useQuery({
        queryKey: [...locationKeys.details(), locationDocId, 'all-data'],
        queryFn: () => locationService.fetchFromFirestore(tenantDocId, locationDocId),
        enabled: !!tenantDocId && !!locationDocId,
    });
};

/**
 * A hook to fetch all restaurants across all tenants (for admin operations)
 * @param limit Optional limit on total restaurants returned
 */
const useGetAllLocationsForAdmin = (limit?: number) => {
    return useQuery({
        queryKey: [...locationKeys.all, 'admin', { limit }],
        queryFn: () => locationService.getAllRestaurantsForAdmin(limit),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * A hook to search restaurants across all tenants (for admin operations)
 * @param searchTerm The search term
 * @param limit Maximum results to return
 */
const useSearchLocationsForAdmin = (searchTerm: string, limit: number = 20) => {
    return useQuery({
        queryKey: [...locationKeys.all, 'admin-search', { searchTerm, limit }],
        queryFn: () => locationService.searchRestaurantsForAdmin(searchTerm, limit),
        enabled: !!searchTerm.trim(),
        staleTime: 1000 * 60 * 2, // 2 minutes
    });
};

/** The (non-null) resolved shape of a slug lookup: `{ restaurant, tenant }`. */
type LocationBySlugResult = NonNullable<Awaited<ReturnType<typeof locationService.getRestaurantBySlug>>>;

/**
 * A hook to fetch a restaurant by its slug (for marketing sites)
 * @param slug The restaurant slug (e.g., "pizza-palace-downtown")
 * @param initialData Optional SSR-seeded `{ restaurant, tenant }` (already resolved
 *   server-side via the Admin SDK and serialized to plain objects). When provided,
 *   the query starts in the success state, so the menu can render immediately
 *   without waiting on the slug round-trip. The client query still revalidates in
 *   the background.
 */
const useGetLocationBySlug = (slug: string, initialData?: LocationBySlugResult) => {
    // NOT gated on sign-in (2026-10-10). The slug read is the PUBLIC
    // `GET /api/v1/locations/slug/:slug` (no auth decorator, so App Check never
    // gates it either). It used to wait for `authData.firebaseUser` as the
    // sequencing anchor for the page — but that made an UNSEEDED menu page
    // (apps/ordering, checkout's confirmation, the featured-items embed) depend
    // on Firebase anonymous sign-in, which App Check (reCAPTCHA Enterprise) and
    // the per-IP anonymous sign-up quota can both refuse. A guest who could not
    // sign in saw a spinner forever.
    //
    // Everything downstream that genuinely needs a session gates on the session
    // ITSELF, not on this query: `useCart` persists only with a uid (and
    // backfills it onto a local cart when it lands); saved cards, loyalty
    // balance and the customer profile are keyed on the uid; checkout renders
    // nothing until `CustomerAuthHandler` has a user. The SSR-seeded restaurant
    // site already ran exactly this way (the seed made the query resolve before
    // auth), so this brings the unseeded pages in line with it.
    return useQuery({
        queryKey: [...locationKeys.details(), 'slug', slug],
        queryFn: () => locationService.getRestaurantBySlug(slug),
        enabled: !!slug && typeof slug === 'string',
        // SSR seed: when present, the query resolves instantly.
        initialData,
        staleTime: 1000 * 60 * 10, // 10 minutes (marketing sites can cache longer)
        retry: 1, // Only retry once for public-facing slugs
    });
};

/**
 * The Online Service Fee line the CART shows: the SERVER-resolved
 * `onlineServiceFee` from `GET /locations/slug/:slug` (flag + enrollment +
 * config, evaluated by the API) — never computed in the browser, which cannot
 * read the flag.
 *
 * `seed` is the line the page's slug payload (`useGetLocationBySlug`, SSR seed
 * or client fetch) already carries. It only decides WHETHER to ask again:
 *  - seed is a line → a fresh, short-lived fetch when the cart opens, and the
 *    FRESH answer is used (the seed can be minutes old; a fee switched off
 *    since must not show);
 *  - seed is null or absent → no request and no line. Almost every location
 *    charges no fee, so this keeps the cart from re-fetching the whole slug
 *    payload on every open. A fee switched ON since the seed is shown by the
 *    checkout preview, which stays authoritative.
 * A failed or absent answer is `null`: no line.
 */
const useLocationOnlineServiceFee = (slug?: string | null, seed?: OnlineServiceFeeLine | null) =>
    useQuery<OnlineServiceFeeLine | null>({
        queryKey: [...locationKeys.details(), 'slug', slug ?? '', 'online-service-fee'],
        queryFn: async () => (await locationService.getRestaurantBySlug(slug as string))?.onlineServiceFee ?? null,
        enabled: !!slug && readOnlineServiceFeeLine(seed) !== null,
        staleTime: 60 * 1000,
        retry: false,
    });

/**
 * The tax rates the storefront CART estimates tax with: the location's ACTIVE
 * rates as the API read them (`GET /locations/slug/:slug` → `taxRates`, the
 * same rates /orders/preview charges with). Never a browser Firestore read —
 * production enforces App Check on Firestore, a guest whose attestation is
 * refused cannot read the rates, and the cart used to show item tax $0 then.
 *
 * `seed` is what the page's slug payload (SSR seed or client fetch) already
 * carries. An array seeds the query, so a seeded page asks nothing; anything
 * else (absent on an older seed, null when the server could not read them)
 * fetches the public payload — it needs no session and no App Check.
 *
 * `data` is `null` while unknown — loading, failed, or the server could not
 * read them — and the cart then shows tax as calculated at checkout. `[]` is a
 * real answer: the location charges no tax.
 */
const useLocationGuestTaxRates = (slug?: string | null, seed?: unknown) => {
    const seeded = readPublicTaxRates(seed) ?? undefined;
    const query = useQuery<ITaxRate[] | null>({
        queryKey: [...locationKeys.details(), 'slug', slug ?? '', 'guest-tax-rates'],
        queryFn: async () => readPublicTaxRates((await locationService.getRestaurantBySlug(slug as string))?.taxRates),
        enabled: !!slug,
        initialData: seeded,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    });
    return { ...query, data: query.data ?? seeded ?? null };
};

/**
 * A hook to check if a restaurant slug is available across all tenants
 * @param slug The slug to check
 * @param excludeLocationNumericId Optional restaurant ID to exclude from the check (for updates)
 * @param enabled Whether the query should be enabled
 */
const useSlugAvailability = (slug: string, excludeLocationNumericId?: number, enabled: boolean = true) => {
    return useQuery({
        queryKey: [...locationKeys.all, 'slug-availability', slug, { excludeRestaurantId: excludeLocationNumericId }],
        queryFn: () => locationService.checkSlugAvailability(slug, excludeLocationNumericId),
        enabled: enabled && !!slug && slug.length > 0,
        staleTime: 1000 * 30, // 30 seconds (availability should be fresh)
        retry: 1, // Only retry once for availability checks
    });
};

export {
    // restaurantKeys is exported inline at its const declaration
    useCreateLocation, useFetchFromFirestore, useLocationGuestTaxRates,
    // Admin hooks
    useGetAllLocationsForAdmin, useGetLocationByGuid, useGetLocationById,
    // Public hooks
    useGetLocationBySlug, useGetLocationsByTenantId, useLocationOnlineServiceFee, useSearchLocationsForAdmin,
    // Slug operations
    useSlugAvailability,
    // Mutations
    useSyncToFirestore, useUpdateLocation, useUpdateLocationByGuid
};

